import { Component, Input, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcRestApi } from 'common/api/rest-api';
import { SalesforceErrorService } from './salesforce-error/salesforce-error.service';
import { SalesforceGroup, SalesforceGroupItem, WidgetSettings, PrePopulateData, CreateRelatedRecords, AccountRelations } from './salesforce-data.model';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { WindowService } from 'shared/services/window/window.service';

@Component({
  selector: 'app-salesforce-lane',
  templateUrl: './salesforce-lane.component.html',
  styleUrls: ['./salesforce-lane.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SalesforceLaneComponent implements OnInit, OnDestroy {
  public salesforceData: SalesforceGroup[];
  public widgetSettings: WidgetSettings;
  public widgetHeader?: AccountRelations;
  public relations: string[];
  public prePopulateData: PrePopulateData;
  public selectedGroup: SalesforceGroup;
  public createPanelState: string = bcEnums.salesforceCreate.CLOSE;
  public bcEnums = bcEnums;
  public isSearchValid: boolean = true;
  public isListFiltered: boolean = false;
  public isLoading: boolean = false;
  @Input()
  public chatId: string;

  constructor(private bcAnalytics: BcAnalytics,
              private bcRestApi: BcRestApi,
              private _salesforceErrorService: SalesforceErrorService,
              private _window: WindowService) { }

  ngOnInit(): void {
    this.loadSalesforceData();
  }

  ngOnDestroy(): void {
    this._salesforceErrorService.clearError(this.chatId);
  }

  loadSalesforceData(groupId?: string, searchTerm?: string) {
    this.isLoading = true;
    this.bcRestApi.getSalesforceGroups(this.chatId, searchTerm).then((data) => {
      this.widgetSettings = data.widgetSettings;
      this.widgetHeader = data.widgetHeader;
      this.salesforceData = data.groups;

      if (searchTerm) {
        this.isListFiltered = true;
      } else {
        this.isListFiltered = false;
        this.bcAnalytics.eventTrack('Salesforce_TabCount', { count: data.groups.length });
      }

      if (groupId) {
        this.loadGroupItems(groupId);
      } else {
        this.loadGroupItems(this.salesforceData[0].id);
      }
      this.isLoading = false;
    }).catch((error) => {
      this.isLoading = false;
      this.bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
      this._salesforceErrorService.notifyError(this.chatId);
    });
  }

  loadGroupItems(groupId: string): void {
    if (this.createPanelState === bcEnums.salesforceCreate.OPEN) {
      this.createPanelState = bcEnums.salesforceCreate.CLOSE;
    }

    this.isSearchValid = true;
    this.selectedGroup = this.salesforceData.find(group => group.id === groupId);
    this.relations = this._getRelations();
    this._setIcons();
  }

  getIconByLinkStatus(isLinked: boolean): string {
    return isLinked ? 'salesforce-link' : 'salesforce-unlink';
  }

  getExpandIcon(isLoading: boolean): string {
    return isLoading ? 'refresh' : 'arrow-right';
  }

  getTooltipByLinkStatus(isItemLinked: boolean): string {
    const translationKeyName = isItemLinked
      ? 'linkedTooltip'
      : this._isSelectedGroupLinked()
        ? 'disabledTooltip'
        : 'unlinkedTooltip';

    return `salesforceLane.linkButton.${translationKeyName}`;
  }

  isGroupActive(groupId: string): boolean {
    return this.selectedGroup.id === groupId;
  }

  toggleDetails(item: SalesforceGroupItem): void {
    item.isDetailsOpen = !item.isDetailsOpen;

    if (item.isDetailsOpen) {
      item.isLoading = true;
      this.bcRestApi.getSalesforceItemDetails(item.id).then((details) => {
        item.sections = details.sections;
        item.salesforceObjectUrl = details.salesforceObjectUrl;
        item.isLoading = false;
        this.relations = this._getRelations();
        this.bcAnalytics.eventTrack('Salesforce_OpenItemDetails');
        this._salesforceErrorService.clearError(this.chatId);
      }).catch((error) => {
        item.isLoading = false;
        this.bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
        this._salesforceErrorService.notifyError(this.chatId);
      });
    }
  }

  hasFields(item: SalesforceGroupItem): boolean {
    return item.fields && item.fields.length > 0;
  }

  private _isSelectedGroupLinked(): boolean {
    return this.selectedGroup.items.find(item => item.linked === true) !== undefined;
  }

  isLinkButtonDisabled(isItemLinked: boolean): boolean {
    return !isItemLinked && this._isSelectedGroupLinked();
  }

  onLinkButtonClick(item: SalesforceGroupItem, event) {
    event.stopPropagation();
    event.preventDefault();

    if (this.isLinkButtonDisabled(item.linked)) {
      return;
    }

    if(item.linked) {
      this._unlinkSalesforceItemFromChat(item);
    } else {
      this._linkSalesforceItemToChat(item);
    }
  }

  private _linkSalesforceItemToChat(item: SalesforceGroupItem) {
    item.linked = true;
    return this.bcRestApi.linkSalesforceObjectToChat(this.chatId, item.id).then(() => {
      this.bcAnalytics.eventTrack('Salesforce_Link', { chatId: this.chatId });
      this._salesforceErrorService.clearError(this.chatId);
    }).catch((error) => {
      item.linked = false;
      this.bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
      this._salesforceErrorService.notifyError(this.chatId);
    });
  }

  private _unlinkSalesforceItemFromChat(item: SalesforceGroupItem) {
    item.linked = false;
    return this.bcRestApi.unlinkSalesforceObjectFromChat(this.chatId, item.id).then(() => {
      this.bcAnalytics.eventTrack('Salesforce_Unlink', { chatId: this.chatId });
      this._salesforceErrorService.clearError(this.chatId);
    }).catch((error) => {
      item.linked = true;
      this.bcAnalytics.eventTrack('Salesforce_Error', { data: error.data, url: error.config.url });
      this._salesforceErrorService.notifyError(this.chatId);
    });
  }

  private _setIcons(): void {
    switch (this.selectedGroup.name) {
      case 'Cases':
        this.selectedGroup.icon = 'salesforce-case';
        break;
      case 'Contacts':
        this.selectedGroup.icon = 'salesforce-contact';
        break;
      case 'Leads':
        this.selectedGroup.icon = 'salesforce-lead';
        break;
      default:
        this.selectedGroup.icon = 'plus';
        break;
    }
  }

  private _getRelations(): string[] {
    const widgetSettingsExists: boolean = !!this.widgetSettings && !!this.widgetSettings.createRelatedRecords;
    if (!widgetSettingsExists) {
      return [];
    }
    const selectedGroupId: string = this.selectedGroup ? this.selectedGroup.id : '';
    const relatedRecords: CreateRelatedRecords = this.widgetSettings.createRelatedRecords[selectedGroupId] || {};
    const relatedRecordsWithValue: CreateRelatedRecords = Object.keys(relatedRecords)
      .filter((key) => relatedRecords[key] !== null)
      .reduce((acc, key) => ({ ...acc, [key]: relatedRecords[key] }), {});
    return Object.keys(relatedRecordsWithValue);
  }

  get hasCreatePermission(): boolean {
    if (!this.widgetHeader) {
      return true;
    }
    return this.widgetHeader[this.selectedGroup.id] === this.selectedGroup.id;
  }

  openPrePopulatedItemFrom(relation: string, item: SalesforceGroupItem) {
    const property: string =
      this.widgetSettings &&
      this.widgetSettings.createRelatedRecords &&
      this.widgetSettings.createRelatedRecords[this.selectedGroup.id] &&
      this.widgetSettings.createRelatedRecords[this.selectedGroup.id][relation];
    this.prePopulateData = { property, data: { id: item.id, name: item.name } };
    this.loadGroupItems(relation);
    this.createPanelState = bcEnums.salesforceCreate.OPEN;
    this.bcAnalytics.eventTrack('Salesforce_CreateButtonClicked', { type: this.selectedGroup.name });
  }

  openNewItemForm() {
    this.prePopulateData = null;
    this.createPanelState = bcEnums.salesforceCreate.OPEN;
    this.bcAnalytics.eventTrack('Salesforce_CreateButtonClicked', { type: this.selectedGroup.name });
  }

  closeNewItemForm() {
    this.createPanelState = bcEnums.salesforceCreate.CLOSE;
    this.bcAnalytics.eventTrack('Salesforce_CancelCreation', { type: this.selectedGroup.name });
  }

  onKeyPress(event) {
    if (event.which === KeyboardKeyCodes.ENTER) {
      const searchTerm = event.target.value.trim();

      if (searchTerm.length > 2) {
        this.loadSalesforceData(this.selectedGroup.id, searchTerm);
        this.isSearchValid = true;
        this.bcAnalytics.eventTrack('Salesforce_Search');
      } else {
        this.isSearchValid = false;
      }
    }
  }

  onClearSearchButtonClicked() {
    this.isSearchValid = true;
    (<HTMLInputElement>document.getElementById('salesforce-search-field')).value = '';
    this.loadSalesforceData(this.selectedGroup.id);
  }

  openSalesforceObject(salesforceObjectUrl: string): void {
    this.bcAnalytics.eventTrack('Salesforce_ViewObject');
    this._window.open(salesforceObjectUrl);
  }
}
